// InvitesBanner — Feb 23, 2026. Reworked Jul 2026 (Randy, Phase B):
// - Mounted on BOTH the creator Dashboard and the Fan Portal (fans previously
//   never saw collaboration invites at all).
// - PUBLIC-silo invites need a paying creator: server answers 402
//   CREATOR_REQUIRED and we surface the creator upgrade CTA inline. The invite
//   stays pending so they can accept again after upgrading.
// - PERSONAL-silo invites accept freely; free accounts route to Family view.
// - Child-seeded invites show a LOUD consent panel before acceptance.
// - Also redeems a parked /join-family token right after signup/login.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { EnvelopeSimple, ShieldWarning, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, getUser } from "@/lib/api";
import { usePublicPricing, fmt$ } from "@/lib/usePublicPricing";
import WidgetRosterInviteRow from "@/components/WidgetRosterInviteRow";

export default function InvitesBanner() {
  const pricing = usePublicPricing();
  const creatorPrice = fmt$(pricing.platform_fee_cents);
  const [invites, setInvites] = useState([]);
  // Oct 2026 (Randy, Widget Roster Stage 2) — roster invitations land in THIS
  // banner rather than a second one of their own. It is already mounted on the
  // Dashboard and the Fan Portal, and invitees already know what it looks
  // like. Separate grant, shared surface: the two kinds keep their own
  // collections and their own accept endpoints, because accepting a
  // collaborator invite hands over write access to a silo and accepting a
  // roster invitation hands over nothing.
  const [roster, setRoster] = useState([]);
  const [rosterCopy, setRosterCopy] = useState("");
  const [busy, setBusy] = useState(null);
  const [upsellFor, setUpsellFor] = useState(null);
  const [upgrading, setUpgrading] = useState(false);
  const nav = useNavigate();
  const me = getUser();
  const isPaid = !!me && (me.is_paid_creator || me.platform_subscription_status === "active" || me.role === "super_admin");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/invites");
        setInvites(r.data.invites || []);
      } catch { /* not fatal */ }
      try {
        const r = await api.get("/me/widget-invitations");
        setRoster((r.data.invitations || []).filter((i) => i.status === "pending"));
        setRosterCopy(r.data.sponsorship_copy || "");
      } catch { /* not fatal — the roster is Creator Pro only */ }
    })();
  }, []);

  // Post-signup family-link redemption (JoinFamily parks the token).
  useEffect(() => {
    (async () => {
      let tok = null;
      try { tok = localStorage.getItem("mysilo_family_token"); } catch { /* */ }
      if (!tok) return;
      try { localStorage.removeItem("mysilo_family_token"); } catch { /* */ }
      try {
        const r = await api.post(`/join-family/${tok}/redeem`);
        toast.success(`You've joined “${r.data.silo_name}” — opening your family view.`);
        nav(`/family-view/${r.data.silo_id}`);
      } catch { /* revoked/invalid — drop silently */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const accept = async (inv) => {
    setBusy(inv.id);
    try {
      const r = await api.post(`/invites/${inv.id}/accept`);
      setInvites((l) => l.filter((i) => i.id !== inv.id));
      toast.success(`You're in — “${inv.silo_name || "the silo"}” is yours to collaborate on.`);
      if (r.data?.silo_kind === "personal" && !isPaid) nav(`/family-view/${r.data.silo_id}`);
      else nav(`/dashboard/silo/${r.data.silo_id}`);
    } catch (e) {
      // Jul 2026 — the axios interceptor in /lib/api.js flattens structured
      // FastAPI `detail` objects to a string on `detail` and mirrors them to
      // `detail_obj` + `guard_code`. Read those to detect the CREATOR gate.
      const det = e.response?.data?.detail;
      const detObj = e.response?.data?.detail_obj;
      const code = e.response?.data?.guard_code || detObj?.code;
      if (e.response?.status === 402 && code === "CREATOR_REQUIRED") {
        setUpsellFor(inv.id);
      } else {
        toast.error((typeof det === "string" && det) || detObj?.message || "Could not accept the invite.");
      }
    } finally { setBusy(null); }
  };

  const decline = async (inv) => {
    setBusy(inv.id);
    try {
      await api.post(`/invites/${inv.id}/decline`);
      setInvites((l) => l.filter((i) => i.id !== inv.id));
      toast("Invite declined.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not decline.");
    } finally { setBusy(null); }
  };

  const upgradeNow = async () => {
    if (upgrading) return;
    setUpgrading(true);
    try {
      const r = await api.post("/stripe/platform/checkout", { origin_url: window.location.origin, return_path: window.location.pathname });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout.");
      setUpgrading(false);
    }
  };

  if (invites.length === 0 && roster.length === 0) return null;

  return (
    // `siloart-keep` — Oct 2026 (Randy). This banner paints its own cream card
    // on top of whatever Silo Art is behind it, so it is an ENCASED surface by
    // the contract in index.css. Without the opt-out, All-white-text turned
    // every <strong> and <p> in here white-on-cream, i.e. invisible: the
    // business name, the silo name and the whole sponsorship paragraph. It hit
    // the collaborator rows too, which have carried the same markup since Feb
    // 2026. One class on the root covers every row inside it. Note `bg-white/70`
    // does NOT satisfy the stylesheet's `.bg-white` exclusion — different class
    // name — which is why the inner panels went invisible as well.
    <div className="space-y-2 mb-6 siloart-keep" data-testid="invites-banner">
      {roster.map((inv) => (
        <WidgetRosterInviteRow key={inv.id} inv={inv} copy={rosterCopy}
          onDone={(id) => setRoster((l) => l.filter((i) => i.id !== id))} />
      ))}
      {invites.map((inv) => (
        <div key={inv.id} className="bg-[#F8F4E8] border border-[#E5C77F] rounded-lg px-4 py-3" data-testid={`invite-row-${inv.id}`}>
          <div className="flex items-start gap-3">
            <EnvelopeSimple size={18} weight="duotone" className="text-[#C19A4A] mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-[#1C1B1A]">
                <strong>{inv.inviter_name || "Someone"}</strong>{" "}
                {inv.is_child
                  ? <>is inviting you into their <strong>family household</strong> — “{inv.silo_name}”</>
                  : <>invited you to collaborate on <strong>“{inv.silo_name}”</strong></>}
              </div>
              {inv.is_child && (
                <div className="mt-2 bg-white/70 border border-[#E5C77F] rounded-md p-3" data-testid={`invite-child-panel-${inv.id}`}>
                  <div className="flex items-center gap-1.5 text-[#8A5A00] text-xs font-medium">
                    <ShieldWarning size={14} weight="duotone" /> Family-restricted membership — read before accepting
                  </div>
                  <p className="text-[11px] text-[#6B6A68] mt-1 leading-relaxed">
                    Accepting marks your account with <strong>purchase guardrails</strong>: every checkout requires
                    typing a card fresh (no saved cards). Nothing else changes. A parent can release it anytime,
                    and leaving the household removes it automatically. If you're an adult, decline and ask for a
                    normal invite instead.
                  </p>
                </div>
              )}
              {upsellFor === inv.id && (
                <div className="mt-2 bg-white/70 border border-[#E5C77F] rounded-md p-3" data-testid={`invite-upsell-${inv.id}`}>
                  <div className="flex items-center gap-1.5 text-[#4A5D4E] text-xs font-medium">
                    <Sparkle size={14} weight="duotone" /> Collaborating on a creator silo needs the Creator plan
                  </div>
                  <p className="text-[11px] text-[#6B6A68] mt-1 leading-relaxed">
                    Creator-silo collaboration runs through the creator dashboard, which comes with the
                    {" "}{creatorPrice}/mo Creator subscription. Your invite stays waiting — upgrade, then accept again. (Family &amp; organizer silos are free to join — no paywall on those.)
                  </p>
                  <button onClick={upgradeNow} disabled={upgrading} data-testid={`invite-upsell-upgrade-${inv.id}`}
                    className="mt-2 text-xs px-3 py-1.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-60">
                    {upgrading ? "Opening Stripe…" : `Become a Creator · ${creatorPrice}/mo`}
                  </button>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => accept(inv)} disabled={busy === inv.id} data-testid={`invite-accept-${inv.id}`}
                className="text-xs px-3 py-1.5 rounded-md bg-[#4A5D4E] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-60">
                {inv.is_child ? "Join household" : "Accept"}
              </button>
              <button onClick={() => decline(inv)} disabled={busy === inv.id} data-testid={`invite-decline-${inv.id}`}
                className="text-xs px-3 py-1.5 rounded-md border hairline text-[#6B6A68] hover:border-[#C16E5A] hover:text-[#C16E5A] disabled:opacity-60">
                Decline
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
